<!--
  - Copyright (c) 2021 by Leyton Group - All Rights Reserved.
  -  This source code, and resulting software, is the confidential and proprietary information
  -  ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  -  of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  -  and shall use it only in accordance with the terms and conditions of any and all license
  -  agreements you have entered into with The Company.
  -->

<template>
  <div id="app">
    <component :is="layout">
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </component>

    <v-tour
      name="GIGAWHAT_Tour"
      :steps="steps"
      :callbacks="callbacks"
      :options="options"
    ></v-tour>

    <import-in-mass-indicator
      v-for="(importDetail, index) in openedImportsDetails"
      :key="index"
      @close-import="handleCloseImport"
      :index="index"
      :importDetail="importDetail"
    ></import-in-mass-indicator>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import router from "./plugins/vue-router";
import { webSocketService } from "./services";
import i18n from "@/i18n";
import store from "@/store";
import { helpers } from "@/shared/helper";
import ImportInMassIndicator from "@/views/Simulations/import-mass-estimations/ImportInMassIndicator.vue";
import eventBus from "@/shared/event.bus";
import { importService } from "@/views/Simulations/services/import.service";

const defaultLayout = "default";
export default {
  name: "App",
  components: { ImportInMassIndicator },
  data() {
    return {
      options: {
        useKeyboardNavigation: true,
        labels: {
          buttonSkip: "Passer le tour",
          buttonPrevious: "Précédent",
          buttonNext: "Suivant",
          buttonStop: "Terminer"
        }
      },
      steps: [
        {
          target: ".dashboard-link",
          header: {
            title: "Page Accueil"
          },
          content: `<ul>
                      <li>Sur la page d’Accueil, vous pourrez visualiser toutes les notifications liées au changement de statut de vos ouvrages ou des lots de traitement.</li>
                      <li>Si vous cliquez sur la notification, vous serez redirigé vers l’ouvrage ou le lot de traitement concerné.</li>
                      <li>Vous pourrez visualiser ainsi les informations générales de votre compte.</li>
                    </ul>`,
          params: {
            placement: "right"
          }
        },
        {
          target: ".works-link",
          header: {
            title: "Page Ouvrage"
          },
          content: `<p>1) Chaque ouvrage sur GigaWhat représente donc un chantier. Dans la page ouvrage vous pourrez visualiser pour chaque ouvrage les informations suivantes :</p>
                    <ul>
                      <li>Volume précaire</li>
                      <li>Volume classique</li>
                      <li>Nombre de lots de traitements</li>
                      <li>Nombre d’opérations</li>
                      <li>Statut de l’ouvrage</li>
                    </ul>
                    <p>2) Vous pourrez visualiser la liste des opérations réparties par fiche CEE avec les différents statuts et volumes.</p>
                    <p>3) Vous pourrez visualiser tous les informations permettant de calculer le volume CEE de l'opération.</p>
                    <p>4) Vous pourrez  visualiser les détails de vos documents , les télécharger, et préciser les types de votre documents lors de l’upload. </p>`,
          params: {
            placement: "right"
          }
        },
        {
          target: ".simulations-link",
          header: {
            title: "Page Simulation"
          },
          content:
            "<p>Ici vous pourrez estimer le volume d’une opération en cours ou à venir ainsi que les gains potentiels selon le court du court CEE à date.</p>",
          params: {
            placement: "right"
          }
        },
        {
          target: ".reporting-link",
          header: {
            title: "Page Reporting"
          },
          content:
            "<p>Possibilité d'exporter des informations spécifiques sur vos opérations.</p>",
          params: {
            placement: "right"
          }
        },
        {
          target: ".reglementaire-link",
          header: {
            title: "Page Information réglementaire"
          },
          content:
            "<p>Vous pourrez visualiser les actualités réglementaire sur les dispositifs CEE.</p>",
          params: {
            placement: "right"
          }
        },
        {
          target: ".contacts-link",
          header: {
            title: "Page contact"
          },
          content: "<p>Contact de l’équipes Leyton qui vous est dédiée.</p>",
          params: {
            placement: "right"
          }
        }
      ],
      callbacks: {
        onStart: this.backToDashboard,
        onPreviousStep: this.handlePreviousStep,
        onNextStep: this.handleNextStep,
        onSkip: this.backToDashboard,
        onFinish: this.backToDashboard
      },
      importInProgress: false,
      intervalIds: {},
      importsDetails: []
    };
  },
  computed: {
    ...mapGetters("account", {
      UserInfo: "getCurrentUser",
      loggedIn: "loggedIn",
      isActivateTour: "isActivateTour"
    }),
    ...mapGetters("leftsidebar", ["getHasHeaderStatus"]),
    layout() {
      return (this.$route.meta.layout || defaultLayout) + "-layout";
    },
    openedImportsDetails() {
      return this.importsDetails.filter(
        importDetail => importDetail.importInProgress
      );
    }
  },
  created() {
    this.reactToWindowResizing();
    window.addEventListener("resize", () => {
      this.reactToWindowResizing();
    });
    eventBus.$on("start-import", ({ clientReferenceId, importId }) => {
      this.startPolling(importId, clientReferenceId);
      this.importsDetails.forEach(i => {
        if (i.id === importId) {
          i.importInProgress = true;
        }
      });
    });
  },
  methods: {
    ...mapActions({ clearAlert: "alert/clear" }),
    // ...mapMutations('leftsidebar',[ 'setSidebarShow'])
    ...mapMutations({
      setSidebarShow: "leftsidebar/setSidebarShow"
    }),
    reactToWindowResizing() {
      const _width = window.innerWidth;
      const _maxWidth = 992;
      if (_width < _maxWidth || this.getHasHeaderStatus) {
        this.setSidebarShow(false);
        document.body.classList.remove("is-sidebar-open");
      } else {
        this.setSidebarShow(true);
        document.body.classList.remove("is-sidebar-open");
      }
    },
    backToDashboard() {
      router.router.push("/", Function);
    },
    handleNextStep(currentStep) {
      if (currentStep === 0) {
        router.router.push("/works", Function);
      }
      const _step1 = 1;
      if (currentStep === _step1) {
        router.router.push("/simulations", Function);
      }
      const _step2 = 2;
      if (currentStep === _step2) {
        router.router.push("/reporting", Function);
      }
      const _step3 = 3;
      if (currentStep === _step3) {
        router.router.push("/reglementaire", Function);
      }
      const _step4 = 4;
      if (currentStep === _step4) {
        router.router.push("/contacts", Function);
      }
      const _step5 = 5;
      if (currentStep === _step5) {
        router.router.push("/account", Function);
      }
    },
    handlePreviousStep(currentStep) {
      const _step1 = 1;
      if (currentStep === _step1) {
        router.router.push("/", Function);
      }
      const _step2 = 2;
      if (currentStep === _step2) {
        router.router.push("/works", Function);
      }
      const _step3 = 3;
      if (currentStep === _step3) {
        router.router.push("/simulations", Function);
      }
      const _step4 = 4;
      if (currentStep === _step4) {
        router.router.push("/reporting", Function);
      }
      const _step5 = 5;
      if (currentStep === _step5) {
        router.router.push("/reglementaire", Function);
      }
      const _step6 = 6;
      if (currentStep === _step6) {
        router.router.push("/contacts", Function);
      }
    },
    handleCloseImport(id) {
      this.importsDetails.forEach(i => {
        if (i.id === id) {
          i.importInProgress = false;
        }
      });
      this.stopPolling(id);
    },
    startPolling(importId, clientReferenceId) {
      this.intervalIds[importId] = setInterval(() => {
        importService.fetchImportProgress(importId).then(importDetail => {
          importDetail.clientReferenceId = clientReferenceId;
          importDetail.importInProgress = true;
          const existingIndex = this.importsDetails.findIndex(
            detail => detail.id === importDetail.id
          );

          if (existingIndex !== -1) {
            this.$set(this.importsDetails, existingIndex, importDetail);
          } else {
            this.importsDetails.push(importDetail);
          }
        });
      }, 2000);
    },
    stopPolling(importId) {
      clearInterval(this.intervalIds[importId]);
      this.$delete(this.intervalIds, importId);
    }
  },
  watch: {
    // eslint-disable-next-line no-unused-vars
    $route(to, from) {
      this.clearAlert();
    },
    loggedIn(isLoggedIn) {
      // deactivate tour temporarily
      /*if (isLoggedIn) {
        if (!this.isActivateTour) {
          if (!localStorage.getItem('GIGAWHAT_Tour')) {
            this.$tours['GIGAWHAT_Tour'].start();
            localStorage.setItem('GIGAWHAT_Tour', true);
          }
        } else {
          this.$tours['GIGAWHAT_Tour'].start();
          localStorage.setItem('GIGAWHAT_Tour', true);
        }
      }*/
      if (!isLoggedIn) {
        webSocketService.disconnect();
        this.$tours["GIGAWHAT_Tour"].finish();
      }
    },
    UserInfo: () => {
      i18n.locale = helpers.isAnArtisan() ? "artisan" : "fr";
    }
  },
  beforeDestroy() {
    eventBus.$off("start-import");
  }
};
</script>
<style lang="scss">
@import "assets/scss/app.scss";
.v-step__buttons {
  display: flex;
  justify-content: center;
}

.v-step__button {
  color: $primary !important;
  background-color: $white !important;
  border: 1px solid $primary !important;
  border-radius: 1rem !important;
  display: flex !important;
  align-items: center !important;
  padding: 15px 20px !important;
}

.v-step__button:hover {
  color: $white !important;
  background-color: $primary !important;
}

.v-step__button-skip {
  color: $success !important;
  background-color: $white !important;
  border: 1px solid $success !important;
}

.v-step__button-skip:hover {
  color: $white !important;
  border: 1px solid $success !important;
  background-color: $success !important;
}

.v-step {
  max-width: 100% !important;
  width: 500px !important;
  color: black !important;
  background-color: $white !important;
  border: none !important;
  border-bottom-left-radius: 1.25rem !important;
  border-bottom-right-radius: 1.25rem !important;
}

.v-step__content {
  margin: 1rem 0 !important;
}

.v-step__header {
  border: none !important;
  color: $white !important;
  background-color: $primary !important;
}

.v-step__arrow--dark:before {
  background-color: $primary !important;
}
</style>
